import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { pick } from 'lodash-es';
import {
  CaseStatus,
  DonorType,
  GroundConfirmation,
  LogisticsProviderType,
  OrganType,
  QuoteStatus,
  RandomizationFilterType,
  RoutePlanStatus,
} from '@/api/apis/case';
import { boardStatusList } from '@/constants/case/case.constants';
import { ServiceRegionId } from '@/constants/serviceRegion.constants';

export type TxCaseBoardListFilters = {
  assignedCustomers?: string[];
};

export type CaseBoardListFilters = {
  organ: OrganType[];
  donorType: DonorType[];
  logisticProviderTypes: LogisticsProviderType[];
  routePlanningStatuses: RoutePlanStatus[];
  quoteStatuses: QuoteStatus[];
  groundConfirmationStatuses: GroundConfirmation[];
  assignedUsers: string[];
  assignedMe: boolean;
  recent: boolean;
  favorites: boolean;
  duplicates: boolean;
  opoRequest: boolean;
  isReallocatedFromSource: boolean;
  // activeCases: boolean; // Temp removed in TMDXLP-8017
  trialProgramIds: string[];
  randomizationResultTypes: RandomizationFilterType[];
  serviceRegionIds: ServiceRegionId[];
  txCenterIds: string[];
  opoIds: string[];
  donorHospitalIds: string[];
};

export type StatusGroup = {
  id: string;
  name: string;
  statuses: CaseStatus[];
};

export type CaseBoardPreferences = {
  isRows: boolean;
  hideEmpty: boolean;
  showGroups: boolean;
  statusesOrdered: CaseStatus[];
  groupsOrdered: StatusGroup[];
};

type CasesFiltersStore = {
  setFilters: (filters: Partial<CaseBoardListFilters>) => void;
} & CaseBoardListFilters;

export const DEFAULT_BOARD_FILTERS = {
  organ: [],
  donorType: [],
  logisticProviderTypes: [],
  routePlanningStatuses: [],
  quoteStatuses: [],
  groundConfirmationStatuses: [],
  assignedUsers: [],
  assignedMe: false,
  recent: false,
  favorites: false,
  duplicates: false,
  opoRequest: false,
  isReallocatedFromSource: false,
  // activeCases: false, // Temp removed in TMDXLP-8017
  trialProgramIds: [],
  randomizationResultTypes: [],
  serviceRegionIds: [],
  txCenterIds: [],
  opoIds: [],
  donorHospitalIds: [],
} satisfies CaseBoardListFilters;

export const createCasesFiltersStore = <T extends Partial<CaseBoardListFilters>>(
  defaultBoardFilters: T,
  storageName = 'board-filters',
) =>
  create<CasesFiltersStore>()(
    persist(
      (set) =>
        ({
          ...defaultBoardFilters,
          setFilters: (filters) => set(() => ({ ...filters })),
        }) as CasesFiltersStore,
      {
        name: storageName,
        version: 4,
      },
    ),
  );

export const useCasesFiltersStore = createCasesFiltersStore(DEFAULT_BOARD_FILTERS, 'board-filters');
export const useTxCasesFiltersStore = createCasesFiltersStore(
  pick(DEFAULT_BOARD_FILTERS, ['assignedMe', 'recent']),
  'tx-board-filters',
);

type CasesBoardState = {
  setBoardPreferences: (preferences: Partial<CaseBoardPreferences>) => void;
} & CaseBoardPreferences;

export const useCasesBoardStore = create<CasesBoardState>()(
  persist(
    (set) => ({
      isRows: true,
      showGroups: false,
      hideEmpty: false,
      statusesOrdered: boardStatusList,
      groupsOrdered: [],
      setBoardPreferences: (preferences) => set(() => ({ ...preferences })),
    }),
    {
      name: 'board-settings',
      version: 6,
    },
  ),
);

type CaseSearchState = {
  searchQuery: string;
  setSearchQuery: (text: string) => void;
};

export const useCasesSearchStore = create<CaseSearchState>((set) => ({
  searchQuery: '',
  setSearchQuery: (value) => set({ searchQuery: value }),
}));
